/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import React from "react";
import { Redirect, Switch, Route } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { Layout } from "../_metronic/layout";
import BasePage from "./BasePage";
import { Logout, AuthPage } from "./modules/Auth";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import ReportPage from "./modules/ReportFault/ReportFault";
import LandingPage from "../components/LandingPage";
import HelpPage from "../components/Help/HelpPage";
import TermsAndConditions from "../components/Legal/TermsAndConditions";
import AccountWorkspace from "./modules/Onboarding/AccountWorkspace";
import NoOrganisation from "./modules/Onboarding/NoOrganisation";
import AcceptInvite from "./modules/Auth/pages/AcceptInvite";
import ScanRedirectCapture from "../components/ScanRedirectCapture";
import ClearPostLoginPath from "../components/ClearPostLoginPath";
import { QrScannerProvider } from "../components/QrScannerProvider";
import { peekPostLoginPath } from "../components/postLoginRedirect";

export function Routes() {
    const { isAuthorized, currentUser } = useSelector(
        ({ auth }) => ({
            isAuthorized: auth.user != null,
            currentUser: auth.user && auth.user.user
        }),
        shallowEqual
    );

    // Self-registered accounts stay usable-but-restricted (can log in, view
    // their own profile/onboarding/review status) until a Super Admin
    // approves them - see src/middleware/getUserIdfromToken.js /
    // src/middleware/onboarding.js for the matching backend-side gate.
    // `isApproved` covers every non-approved accountStatus (onboarding,
    // pending_review, changes_required, rejected, suspended) since it's
    // kept in sync with accountStatus by setAccountStatus() on the backend -
    // AccountWorkspace itself renders the right sub-experience for each.
    const isPendingApproval =
        isAuthorized && currentUser && !currentUser.super && currentUser.isApproved === false;

    // Approved account whose organisation(s) are all archived/closed: no operational app, only the
    // neutral page (invitations, own details, log out). Mirrors the backend's no-organisation gate.
    const hasNoActiveOrganisation =
        isAuthorized && currentUser && !currentUser.super && currentUser.noActiveOrganisation === true;

    return (
        <Switch>
            {/* Landing Page Route - accessible to everyone */}
            <Route exact path="/" component={LandingPage} />
            <Route path="/help" component={HelpPage} />
            <Route path="/terms-and-conditions" component={TermsAndConditions} />

            <Route
                path="/report/fault/:id"
                render={props => (
                    <QrScannerProvider>
                        <ReportPage {...props} id={props.match.params.id} />
                    </QrScannerProvider>
                )}
            />
            {/* Accept-invite must work whether or not the visitor is already
                logged in (an existing account needs to log in as the invited
                email right here to accept) - kept OUTSIDE the AuthPage
                is-authorized redirect below, which would otherwise bounce a
                logged-in visitor straight to /dashboard before they can accept. */}
            <Route path="/auth/accept-invite/:token" component={AcceptInvite} />
            {!isAuthorized ? (
                /*Render auth page when user at `/auth` and not authorized.*/
                <Route path="/auth">
                    <AuthPage />
                </Route>
            ) : (
                /*Otherwise redirect: back to a pending QR scan if one was interrupted by login, else the dashboard.*/
                <Redirect from="/auth" to={peekPostLoginPath() || "/dashboard"} />
            )}

            <Route path="/error" component={ErrorsPage} />
            <Route path="/logout" component={Logout} />

            {isPendingApproval ? (
                <Route path="/">
                    <AccountWorkspace />
                </Route>
            ) : hasNoActiveOrganisation ? (
                <Route path="/">
                    <NoOrganisation />
                </Route>
            ) : !isAuthorized ? (
                /*Show landing page for non-authorized users at root, then redirect to auth*/
                <>
                    <Route exact path="/" component={LandingPage} />
                    <ScanRedirectCapture />
                    <Redirect to="/auth/login" />
                </>
            ) : (
                <QrScannerProvider>
                    <ClearPostLoginPath />
                    <Layout>
                        <BasePage />
                    </Layout>
                </QrScannerProvider>
            )}
        </Switch>
    );
}
